import React, { type ReactNode } from 'react';

import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import type { DropdownItemProps } from '@atlaskit/dropdown-menu/types';
import type { AnyEntryPoint } from '@atlassian/entry-point-types/types';

import { ModalTrigger, type ModalTriggerProps as ModalTriggerProps } from './modal-trigger';

export type ModalDropdownItemTriggerProps<TEntryPoint> = Omit<
	ModalTriggerProps<TEntryPoint>,
	'children' | 'title'
> & {
	/**
	 * Title shown in the modal while the entry point loads or fails.
	 */
	modalTitle?: ReactNode;
} & Omit<DropdownItemProps, 'ref'>; // Prevent ref prop collision between ButtonProps and render prop

// eslint-disable-next-line @repo/internal/react/require-jsdoc
export const ModalDropdownItemTrigger = <TEntryPoint extends AnyEntryPoint>({
	children,
	entryPoint,
	entryPointParams,
	entryPointProps,
	Fallback,
	modalTitle,
	modalProps,
	onRequestClose,
	forwardedRef,
	preloadOnFocus,
	loadingText,
	source,
	...dropdownProps
}: ModalDropdownItemTriggerProps<TEntryPoint>): React.JSX.Element => {
	return (
		<ModalTrigger
			forwardedRef={forwardedRef}
			entryPoint={entryPoint}
			entryPointParams={entryPointParams}
			entryPointProps={entryPointProps}
			Fallback={Fallback}
			title={modalTitle}
			modalProps={modalProps}
			onRequestClose={onRequestClose}
			preloadOnFocus={preloadOnFocus}
			loadingText={loadingText}
			source={source}
		>
			{({ ref, ...triggerProps }) => (
				<DropdownItem ref={ref} {...dropdownProps} {...triggerProps}>
					{children}
				</DropdownItem>
			)}
		</ModalTrigger>
	);
};
